// @ts-check
import { useEffect } from 'react';
import { useBlocker } from 'react-router-dom';

/**
 * Blocks in-app navigation (sidebar links, browser back/forward) away from the
 * calling page while `hasUnsavedChanges` is true, and warns on a real browser-level
 * exit (refresh/close/typed URL) via the native beforeunload dialog.
 *
 * Requires the app to be wrapped in a data router (createBrowserRouter +
 * RouterProvider) — useBlocker throws under a plain <BrowserRouter>.
 *
 * React Router supports only one active blocker per router at a time — do not
 * call this hook from more than one mounted component simultaneously (e.g. a
 * page and a nested editor both guarding independently); only the most
 * recently registered blocker takes effect.
 *
 * @param {boolean} hasUnsavedChanges
 * @returns {import('react-router-dom').Blocker} render <UnsavedChangesPrompt
 *   blocker={blocker} /> to show the confirmation modal when a navigation
 *   attempt is actually blocked.
 */
export function useUnsavedChangesGuard(hasUnsavedChanges) {
  const blocker = useBlocker(hasUnsavedChanges);

  useEffect(() => {
    if (!hasUnsavedChanges) return undefined;
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  useEffect(() => {
    if (blocker.state === 'blocked' && !hasUnsavedChanges) {
      blocker.reset?.();
    }
  }, [blocker, hasUnsavedChanges]);

  return blocker;
}
